import { SUPPORTER_LANGUAGES } from '@/constant/lang'
import type {
  ActivityLocalizedTextMap,
  ActivityV2BannerConfig,
  BackgroundUrlItem,
} from '@/types/activity'

export const getTrimmedString = (value: unknown) => {
  if (typeof value !== 'string') {
    return undefined
  }

  const normalizedValue = value.trim()

  return normalizedValue || undefined
}

export const getTrimmedStringValue = (value: unknown) =>
  getTrimmedString(value) || ''

export const getTrimmedValue = (value: string | null) => value?.trim() || ''

export const getTrimmedStringArray = (value: unknown) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item) => getTrimmedString(item))
    .filter((item): item is string => Boolean(item))
}

export const getImageUrlsFromBackgroundList = (
  backgroundUrlList: BackgroundUrlItem[] | undefined
) => {
  if (!backgroundUrlList?.length) {
    return []
  }

  return Array.from(
    new Set(
      backgroundUrlList.flatMap((item) => {
        const singleUrl = getTrimmedString(item.url)
        const urls = getTrimmedStringArray(item.urls)

        return singleUrl ? [singleUrl, ...urls] : urls
      })
    )
  )
}

export const getFirstBackgroundUrl = (backgroundUrlList: unknown) => {
  if (!Array.isArray(backgroundUrlList)) {
    return undefined
  }

  for (const item of backgroundUrlList) {
    if (!item || typeof item !== 'object') {
      continue
    }

    const url = getTrimmedString((item as { url?: unknown }).url)
    if (url) {
      return url
    }

    const urls = getTrimmedStringArray((item as { urls?: unknown }).urls)
    if (urls.length > 0) {
      return urls[0]
    }
  }

  return undefined
}

export const getBannerField = (
  banner: ActivityV2BannerConfig | undefined,
  fieldNames: string[]
) => {
  if (!banner) {
    return undefined
  }

  for (const fieldName of fieldNames) {
    const value = getTrimmedString(banner[fieldName])
    if (value) {
      return value
    }
  }

  return undefined
}

export const getLocaleTextCandidates = (lang?: string) => {
  const normalizedLang = getTrimmedString(lang)?.toLowerCase() || ''
  const matchedLanguage = SUPPORTER_LANGUAGES.find(
    (item) => item.language_code.toLowerCase() === normalizedLang
  )
  const candidates = new Set<string>()

  if (normalizedLang) {
    candidates.add(normalizedLang)
  }

  if (lang?.trim()) {
    candidates.add(lang.trim())
  }

  if (matchedLanguage?.language) {
    candidates.add(matchedLanguage.language)
  }

  if (matchedLanguage?.display) {
    candidates.add(matchedLanguage.display)
  }

  if (normalizedLang === 'zh' || normalizedLang === 'zh-cn') {
    candidates.add('Chinese')
    candidates.add('SimplifiedChinese')
  }

  if (normalizedLang === 'zh-tw') {
    candidates.add('TraditionalChinese')
  }

  if (normalizedLang === 'pt') {
    candidates.add('Portuguese')
  }

  if (normalizedLang === 'pt-br') {
    candidates.add('BrazilianPortuguese')
    candidates.add('Portuguese')
  }

  candidates.add('English')

  return Array.from(candidates)
}

export const resolveLocalizedTextValue = (
  textMap: ActivityLocalizedTextMap | undefined,
  lang?: string
) => {
  if (!textMap) {
    return ''
  }

  const entries = Object.entries(textMap)
    .map(([key, value]) => [key.trim(), getTrimmedString(value)] as const)
    .filter((entry): entry is readonly [string, string] =>
      Boolean(entry[0] && entry[1])
    )

  if (entries.length === 0) {
    return ''
  }

  const normalizedMap = new Map(
    entries.map(([key, value]) => [key.toLowerCase(), value])
  )

  for (const candidate of getLocaleTextCandidates(lang)) {
    const value = normalizedMap.get(candidate.toLowerCase())

    if (value) {
      return value
    }
  }

  return (
    entries
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([, value]) => value)
      .find(Boolean) || ''
  )
}
